import { IoIosArrowBack } from "react-icons/io";
import { genInvoiceUrl, sendReceiptToEmailUrl } from "../../../api/endpoints";
import { FaArrowLeft, FaCheck } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import InvoicePDF from "./printTest/GeneratePDF";
import Receipt, { ThermalPrinterDemo, ThermalReceipt } from "./printTest/ReceiptGenerator";
import QRCode from "react-qr-code";
import { renderToStaticMarkup } from "react-dom/server";
import { ButtonLoader } from "../../../components/uxCollections/Loaders";
import { CheckCircle, ArrowLeft, Printer, MessageSquare, Mail } from "lucide-react";
import "../../../styles/stores/makeTransactions/tansactionSuccess.css";
import { syncOfflineQueue } from "../../../tools/synOfflineQueue";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";
import JsBarcode from "jsbarcode";

const TransactionSuccess = ({ whichCompToRender, transactionDetails_, setAlertDisplay, setOpenPromptToSync }) => {
  const [displayReceipt, setDisplayReceipt] = useState(false); // to show the receipt component
  const [pdfBlob, setPdfBlob] = useState(null); // to store the PDF blob data
  const [print80mmReceipt, setPrint80mmReceipt] = useState(false); // to trigger the print receipt function
  const [sendingToEmail, setSendingToEmail] = useState(false); // to show the sending to email state

  let { customer, operationType, transactionDetails, products } = transactionDetails_;

  const { user } = useSelector((state) => state.webStoreUser);
  let currency = user?.userCountryCurrencySymbol || "₦"; // Fallback to ₦ if not defined
  const store = useSelector((state) => state.store.store);
  const navigate = useNavigate();
  const isFirstRender = useRef(true);
  const { connectPrinter, printText, disconnectPrinter, isConnected } = useWebUSBPrinter();

  const qrSvg = renderToStaticMarkup(<QRCode value={transactionDetails_.transID} size={100} bgColor='#FFFFFF' fgColor='#000000' level='M' />);

  const receiptRef = useRef();
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const truncateString = (str, maxLength = 10) => {
    if (str.length > maxLength) {
      return str.substring(0, maxLength - 3) + "...";
    }
    return str;
  };

  const receiptFonts = `
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet">
`;

  const receiptBody = (fontSize) => `
  <div class="receipt">
    ${
      store.storeImage
        ? `<img class="store-logo" src="${store.storeImage}" alt="Store Logo" />`
        : `<div class="store-badge">${(transactionDetails_.store.name || "")
            .split(" ")
            .filter(Boolean)
            .map((w) => w[0])
            .slice(0, 2)
            .join("")
            .toUpperCase()}</div>`
    }
    <p class="store-name">${transactionDetails_.store.name}</p>
    <p class="store-line">${store.storeAddress || "no address"}</p>
    <p class="store-line">${user.phoneNumber || ""}</p>
 
    <div class="divider"></div>
 
    <p class="row"><span>Receipt No.</span><span>${truncateString(transactionDetails_.transID) || "-"}</span></p>
    <p class="row"><span>Date</span><span>${new Date(transactionDetails_.transactionDetails.date).toLocaleString()}</span></p>
    <p class="row"><span>Status</span><span>${transactionDetails_.transactionDetails.deliveryStatus}</span></p>
    <p class="row"><span>Payment</span><span>${transactionDetails_.transactionDetails.paymentMode}</span></p>
    <p class="row"><span>Items</span><span>${transactionDetails_.transactionDetails.numberOfItems}</span></p>
 
    <div class="divider"></div>
 
    <p class="section-label">Bill To</p>
    <p class="store-line" style="text-align:left;">${transactionDetails_.customer.fullName}</p>
    <p class="store-line" style="text-align:left;">${transactionDetails_.customer.phoneNumber}</p>
    ${transactionDetails_.customer.address ? `<p class="store-line" style="text-align:left;">${transactionDetails_.customer.address}</p>` : ""}
 
    <div class="divider"></div>
 
    <p class="section-label">Items</p>
    ${transactionDetails_.products
      .map(
        (item) => `
      <div class="item">
        <div class="item-row">
          <span class="item-name">${item.name}</span>
          <span class="item-leader"></span>
          <span class="item-total"><span class="naira">${currency}</span>${item.soldUnitPrice.toLocaleString()}</span>
        </div>
        <div class="item-sub">x${item.quantity} ${item.unit}</div>
      </div>
    `
      )
      .join("")}
 
    <div class="divider"></div>
 
    <p class="row"><span>Subtotal</span><span class="bold"><span class="naira">${currency}</span>${transactionDetails_.transactionDetails.subTotal?.toLocaleString()}</span></p>
 
    ${
      store.isVATenabled
        ? `<p class="row"><span>VAT ${
            transactionDetails_.transactionDetails.vatPercentage
          }%</span><span><span class="naira">${currency}</span>${transactionDetails_.transactionDetails.vat.toLocaleString()}</span></p>`
        : `<p class="row"><span>VAT</span><span>Excluded</span></p>`
    }
    ${
      transactionDetails_.transactionDetails.serviceCharge
        ? `<p class="row"><span>Service charge</span><span><span class="naira">${currency}</span>${transactionDetails_.transactionDetails.serviceCharge.toLocaleString()}</span></p>`
        : ""
    }
    ${
      transactionDetails_.transactionDetails.deliveryFee
        ? `<p class="row"><span>Delivery fee</span><span><span class="naira">${currency}</span>${transactionDetails_.transactionDetails.deliveryFee.toLocaleString()}</span></p>`
        : ""
    }
    ${
      transactionDetails_.transactionDetails.extraPayment
        ? `<p class="row"><span>Extra payment</span><span><span class="naira">${currency}</span>${transactionDetails_.transactionDetails.extraPayment.toLocaleString()}</span></p>`
        : ""
    }
    ${
      transactionDetails_.transactionDetails.discount
        ? `<p class="row"><span>Discount</span><span>-<span class="naira">${currency}</span>${transactionDetails_.transactionDetails.discount.toLocaleString()}</span></p>`
        : ""
    }
    ${
      transactionDetails_.transactionDetails.clearedbalance
        ? `<p class="row"><span>Cleared old balance</span><span><span class="naira">${currency}</span>${Number(
            transactionDetails_.transactionDetails.clearedbalance || 0
          ).toLocaleString()}</span></p>`
        : ""
    }
    ${
      transactionDetails_.operationType.whichOperation === "loan"
        ? `<p class="row"><span>Initial pay</span><span><span class="naira">${currency}</span>${transactionDetails_.operationType.intialPayment?.toLocaleString()}</span></p>`
        : ""
    }
    ${
      transactionDetails_.operationType.whichOperation === "loan"
        ? `<p class="row bold"><span>Balance</span><span><span class="naira">${currency}</span>${transactionDetails_.operationType.balance.toLocaleString()}</span></p>`
        : ""
    }
 
    <div class="total-row"><span>Total</span><span><span class="naira">${currency}</span>${Number(
    transactionDetails_.transactionDetails.totalAmount
  ).toLocaleString()}</span></div>
 
    ${
      transactionDetails_.comments
        ? `<div class="divider"></div><p class="section-label">Comment</p><p class="store-line" style="text-align:left;">${transactionDetails_.comments}</p>`
        : ""
    }
 
    <div class="divider"></div>
 
    <div id="qr">${qrSvg}</div>
    <p class="qr-caption">Scan to retrive this transaction</p>
 
    <div class="divider"></div>
 
    <p class="footer-line bold">Thank you for buying from us!</p>
    <p class="footer-line">Powered by StoreLense</p>
    <p class="footer-line" style="text-transform:none;letter-spacing:0;color:#4a463d;font-weight:400;">Call us ${user.phoneNumber}</p>
  </div>
`;

  const receiptStyles = (fontSize, fontWeight, qrMargin) => `
  @media print {
    body {
      margin: 5mm 2mm;
      width: 80mm;
      font-family: 'DM Mono', 'Courier New', Courier, monospace;
      font-size: ${fontSize}px;
      font-weight: ${fontWeight};
      text-align: center;
      display:flex;
      flex-direction:column;
      align-items:center;
      line-height: 1.35;
      background:rgb(255, 255, 255);
      color:rgb(0, 0, 0);
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
 
    .receipt { width: 80mm; padding:5px; box-sizing: border-box;  }
 
    .store-logo { width: 52px; height: auto; margin: 0 auto 8px; display: block; border-radius: 50%; }
    .store-badge {
      width: 40px; height: 40px; border-radius: 50%; background: #0f2a47; color: #37b4c5;
      display: flex; align-items: center; justify-content: center;
      font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 13px; letter-spacing: .02em;
      margin: 0 auto 8px;
    }
    .store-name { text-align: center; font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 16px; letter-spacing: .05em; text-transform: uppercase; color: #0f2a47; margin: 0 0 6px; }
    .store-line { text-align: center; margin: 0; font-size: 13px; color:rgb(0, 0, 0); line-height: 1.5; font-weight: 550; }
 
    .divider { border-top: 1.5px dashed #c7c1b3; margin: 13px 0; }
 
    .section-label { font-family: 'DM Sans', sans-serif; font-size: 9.5px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color:rgb(2, 2, 2); margin: 0 0 6px; }
 
    .row { display: flex; justify-content: space-between; margin: 2px 0; font-size: 12px; color:rgb(0, 0, 0); font-weight:550 }
    .row p { margin: 0; }
    /* .row span:last-child { color:rgb(18, 18, 18); font-weight: 500; } */
    .bold { font-weight: 700; }
 
    .item { margin-bottom: 8px; }
    .item-row { display: flex; align-items: baseline; }
    .item-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%; font-weight: 550; font-size: 13px; color:rgb(0, 0, 0); }
    .item-leader { flex: 1; min-width: 6px; margin: 0 4px 3px; border-bottom: 1px dotted rgb(41, 41, 41); }
    .item-total { white-space: nowrap; font-weight: 700; }
    .item-sub { font-size: 12px; color:rgb(0, 0, 0); margin-top: 1px; font-weight: 500; diplay: flex; justify-content: flex-start; gap: 4px; }
 
    .total-row {
      display: flex; justify-content: space-between;
      font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 700; color: #0f2a47;
      margin-top: 5px; padding-top: 8px; border-top: 1.5px solid #1c1c1a;
    }
 
    .naira { font-family: Arial, sans-serif; }
 
    #qr { display: flex; justify-content: center; margin: ${qrMargin} auto 0; max-width: 70mm; }
    .qr-caption { text-align: center; font-family: 'DM Sans', sans-serif; font-size: 9px; letter-spacing: .04em; color:rgb(46, 46, 46); margin-top: 7px; }
 
    .footer-line { text-align: center; margin: 4px 0; }
    .footer-line.bold { font-family: 'Nunito', sans-serif; font-size: 12px; color: #0f2a47; }
    .footer-line:not(.bold) { font-size: 9.5px; letter-spacing: .06em; text-transform: uppercase; color:rgb(39, 39, 39); font-weight: 700; }
  }
`;

  const printReceipt = async () => {
    let isMobileDevice = isMobile();
    let printHtml = isMobileDevice
      ? `
      <!DOCTYPE html>
      <html>
      <meta charset="UTF-8">
        <head>
          <title>Receipt</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            @page { size: 80mm auto; margin: 0; }
            ${receiptStyles(12, 400, "12px")}
          </style>
        </head>
        <body onload="window.print()">
          ${receiptBody(12)}
        </body>
      </html>
    `
      : `
      <!DOCTYPE html>
      <html>
      <meta charset="UTF-8">
        <head>
          <title>Receipt</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            ${receiptStyles(14, 500, "16px")}
          </style>
        </head>
        <body onload="window.print()" onafterprint="window.close()">
          ${receiptBody(14)}
        </body>
      </html>
    `;

    try {
      const printContent = receiptRef.current.innerHTML;
      const printWindow = window.open("", "_blank" /* "width=300,height=600" */);
      if (!printWindow) {
        // fallback for mobile browsers that block popups

        const blob = new Blob([printHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);

        window.location.href = url;
        return;
      }
      printWindow.document.write(printHtml);
      printWindow.document.close();
    } catch (error) {
      console.log(error);
    }
  };

  const { count, queue } = useSelector((state) => state.offlineQueue);
  useEffect(() => {
    return () => {
      // while unmounting, check if count is true and then prompt user to sync
      if (count) {
        try {
          setOpenPromptToSync(true);
          console.log("prompt to sync store");
        } catch (error) {
          console.log(error);
        }
      }
    };
  }, []);

  useEffect(() => {
    /*  if (isFirstRender.current) {
      isFirstRender.current = false; // Set to false after the first render
      return; // Skip the effect on the first render
    } */

    if (displayReceipt && pdfBlob) {
      console.log("Sending PDF to email...");

      sendToEmail();
    }
  }, [displayReceipt, pdfBlob]);

  const sendToEmail = async () => {
    try {
      setSendingToEmail(true);
      // 2. Create FormData to send to backend
      const formData = new FormData();
      formData.append("pdf", pdfBlob, "receipt.pdf");
      formData.append("email", transactionDetails_.customer.email); // Optional
      formData.append("customerName", transactionDetails_.customer.fullName); // Optional

      let response = await fetch(sendReceiptToEmailUrl + store._id, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (response.ok) {
        setAlertDisplay({
          showAlert: true,
          type: "success",
          title: "Success",
          message: "Receipt sent to email successfully.",
        });
      } else {
        setAlertDisplay({
          showAlert: true,
          type: "error",
          title: "Error",
          message: "Failed to send receipt to email. Ensured the email is valid.",
        });
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
    } finally {
      setSendingToEmail(false);
    }
  };

  const handleSendToEmail = () => {
    //navigate("/generatReciept");
    setDisplayReceipt(true);
    setTimeout(() => {
      console.log("closing receipt");

      setDisplayReceipt(false);
    }, 5000);
  };

  const generateBookingMessage = () => {
    const itemsList = products
      .map(
        (item) =>
          `• ${item.name}\n  ${currency}${item.soldUnitPrice.toLocaleString()} × ${item.quantity} =  ${currency}${(
            item.soldUnitPrice * item.quantity
          ).toLocaleString()}`
      )
      .join("\n\n");

    let message = `🛍️ *Purchase Receipt - from ${store.storeName} *\n\n`;
    message += `*CUSTOMER DETAILS:*\nName: ${customer.fullName}\n`;
    if (!customer.email === "No Email") message += `Email: ${customer.email}\n`;
    message += `Phone: ${customer.phoneNumber}\n\n`;
    if (!customer.address === "No Address") message += `Address: ${customer.address}\n\n`;
    message += `*PURCHASED ITEMS:*\n${itemsList}\n\n`;
    message += `*PURCHASE SUMMARY:*\nSubtotal: ${currency}${transactionDetails.totalAmount?.toLocaleString()}\n`;

    if (transactionDetails.discount > 0) message += `Discount: -${currency}${transactionDetails.discount?.toLocaleString()}\n`;
    if (transactionDetails.vat > 0) message += `VAT (${transactionDetails.vatPercentage}%): +${currency}${transactionDetails.vat?.toLocaleString()}\n`;
    if (transactionDetails.clearedbalance > 0) message += `Cleared old balance: -${currency}${transactionDetails.clearedbalance?.toLocaleString()}\n`;

    message += `*Total Amount: ${currency}${Number(transactionDetails.totalAmount)?.toLocaleString()}*\n\n`;

    /*  message += `*PAYMENT DETAILS:*\nBank: ${bookingData.bankName}\nAccount: ${bookingData.bankAccountNumber}\n\n`;
     */
    if (transactionDetails_.comments) message += `*NOTE:*\n${transactionDetails_.comments}\n\n`;

    message += `Thank you for your purchase.! 🎉 \n Best regards`;
    message += `\n${store.storeName} Team`;
    message += `\n${user.phoneNumber ? `Contact: ${user.phoneNumber}` : ""}`;

    return message;
  };
  const handleViaSMS = async () => {
    if (customer.phoneNumber === "No phone number") {
      uiAlert("Customer phone number is required to send receipt to whatsapp.");
      return;
    }
    /*  uiAlert("This feature is not available yet. Please try again later."); */

    const message = encodeURIComponent(generateBookingMessage());
    const phoneNumber = customer.phoneNumber.replace(/\D/g, "");

    try {
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
    } catch (error) {
      console.error("Error sending SMS:", error);

      uiAlert("Failed to share on WhatsApp at this time. Send to email or print receipt instead.", "error");
    }
  };

  return (
    <>
      {/* <div className='selectProductDivMain transactionSuccess'>
    
        <div style={{ marginBottom: "30px" }} className='updatTranshd666'>
          <div className='progressTrans'>
            <span className='circleCompleteD'>
              <FaCheck size={8} />
            </span>
            <span className='Progressbarfill rhTrans5364'></span>
            <span className='circleCompleteD'>
              <FaCheck size={8} />
            </span>
            <span className='Progressbarfill rhTrans5364'></span>
            <span className='circleCompleteD'>
              <FaCheck size={8} />
            </span>
            <span className='Progressbarfill rhTrans5364'></span>
            <span className='circleCompleteD'>
              <FaCheck size={8} />
            </span>
          </div>
        </div>
        <div className='paymentCompleted'>
          <span>
            <svg width='32' height='33' viewBox='0 0 32 33' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M3.5 20L8.5 25M15.5 17L20.5 12M11.5 20L16.5 25L28.5 12'
                stroke='#0E49B5'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
            </svg>
          </span>
          <h2> Payment completed</h2>

          <div>
            <div className='transactionDetailsDiv'>
              <div style={{ width: "65%" }}>
                <h5>Summary</h5>
                <div className='transdetailsflexboy'>
                  <div>
                    <span>Date</span>
                    

                    <span>Delivery status</span>
                    <span>Number of items</span>
                    <span>Total price</span>
                    <span>Payment mode</span>
                  </div>
                  <div>
                    <p>{transactionDetails.date.toLocaleDateString()}</p>
               

                    <p>{transactionDetails.deliveryStatus.toUpperCase()}</p>
                    <p>{transactionDetails.numberOfItems}</p>
                    <p>
                      {user.userCountryCurrencySymbol} {transactionDetails.totalAmount.toLocaleString("en-US")}
                    </p>
                    <p>{transactionDetails.paymentMode}</p>
                  </div>
                </div>
              </div>
              <div>
                <h5>Customer info</h5>
                <div className='transdetailsflexboy'>
                  <div>
                    <span>Name</span>
                    <span>Email</span>

                    <span>Phone no</span>
                    <span>Address</span>
                  </div>
                  <div>
                    <p>{customer.fullName} </p>
                    <p>{customer.email}</p>

                    <p>{customer.phoneNumber}</p>
                    <p>{customer.address}</p>
                  </div>
                </div>
              </div>
            </div>
            <p className='transactionNoteInfo'>
              Note: The receipt of this transaction will be sent to the customer email. You can always view the receipt from the transaction page.
            </p>
            <div className='TransPrintButtonDiv'>
              <button onClick={() => printReceipt()} className='TransPrintButton'>
                Print reciept
              </button>
              <button onClick={handleViaSMS} className='TransPrintButton'>
                Send va SMS
              </button>
              {sendingToEmail ? (
                <button style={{ opacity: "0.7", cursor: "not-allowed" }} className='TransPrintButton'>
                  <ButtonLoader />
                </button>
              ) : (
                <button onClick={handleSendToEmail} className='TransPrintButton'>
                  Send to email
                </button>
              )}
            </div>
            <div
              onClick={() => whichCompToRender("CashTransactionComp")}
              style={{ gap: "8px", width: "50%", alignSelf: "center", cursor: "pointer" }}
              className='TransPrintButtonDiv'
            >
              <FaArrowLeft size={16} color='#0e49b5' />
              <p>go to transaction page</p>
            </div>
          </div>
        </div>
      </div> */}
      <div className='payment-completion-container'>
        <div className='payment-completion-wrapper'>
          {/* Progress Steps */}
          <div className='payment-progress-steps'>
            <div className='progress-step progress-step-completed'>
              <div className='progress-step-circle'></div>
            </div>
            <div className='progress-step-connector progress-connector-completed'></div>
            <div className='progress-step progress-step-completed'>
              <div className='progress-step-circle'></div>
            </div>
            <div className='progress-step-connector progress-connector-completed'></div>
            <div className='progress-step progress-step-completed'>
              <div className='progress-step-circle'></div>
            </div>
            <div className='progress-step-connector progress-connector-completed'></div>
            <div className='progress-step progress-step-completed'>
              <div className='progress-step-circle'></div>
            </div>
          </div>

          {/* Success Icon */}
          <div className='payment-success-icon-container'>
            <CheckCircle className='payment-success-icon' />
          </div>

          {/* Main Heading */}
          <h1 className='payment-completion-title'>Payment completed</h1>

          {/* Content Grid */}
          <div className='payment-details-grid'>
            {/* Summary Section */}
            <div className='payment-summary-section'>
              <h2 className='payment-section-title'>Summary</h2>
              <div className='payment-summary-content'>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Date</span>
                  <span className='payment-detail-value'>{transactionDetails.date.toLocaleDateString()} </span>
                </div>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Delivery status</span>
                  {transactionDetails.deliveryStatus === "supplied" ? (
                    <span className='payment-detail-value payment-status-supplied'>SUPPLIED</span>
                  ) : (
                    <span style={{ backgroundColor: "red" }} className='payment-detail-value payment-status-supplied'>
                      Not Supplied{" "}
                    </span>
                  )}
                </div>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Number of items</span>
                  <span className='payment-detail-value'> {transactionDetails.numberOfItems} </span>
                </div>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Total price</span>
                  <span className='payment-detail-value payment-total-price'>
                    {user.userCountryCurrencySymbol} {transactionDetails.totalAmount.toLocaleString("en-US")}
                  </span>
                </div>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Payment mode</span>
                  <span className='payment-detail-value'>{transactionDetails.paymentMode}</span>
                </div>
              </div>
            </div>

            {/* Customer Info Section */}
            <div className='payment-customer-section'>
              <h2 className='payment-section-title'>Customer info</h2>
              <div className='payment-customer-content'>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Name</span>
                  <span className='payment-detail-value'>{customer.fullName}</span>
                </div>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Email</span>
                  <span className='payment-detail-value payment-no-data'>{customer.email}</span>
                </div>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Phone no</span>
                  <span className='payment-detail-value payment-no-data'>{customer.phoneNumber}</span>
                </div>
                <div className='payment-detail-row'>
                  <span className='payment-detail-label'>Address</span>
                  <span className='payment-detail-value payment-no-data'>{customer.address}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Receipt Note */}
          <div className='payment-receipt-note'>
            <p className='payment-note-text'>
              Note: The receipt of this transaction will be sent to the customer email. You can always view the receipt from the transaction page.
            </p>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className='payment-sticky-footer'>
            <div className='payment-action-buttons'>
              <button className='payment-action-btn payment-btn-secondary' onClick={() => printReceipt()}>
                <Printer className='payment-btn-icon' />
                Print receipt
              </button>
              <button className='payment-action-btn payment-btn-secondary' onClick={handleViaSMS}>
                <MessageSquare className='payment-btn-icon' />
                Send via WhatsApp
              </button>
              {sendingToEmail ? (
                <button className='payment-action-btn payment-btn-primary' disabled>
                  <ButtonLoader />
                </button>
              ) : (
                <button className='payment-action-btn payment-btn-primary' onClick={handleSendToEmail}>
                  <Mail className='payment-btn-icon' />
                  Send to email
                </button>
              )}
            </div>

            {/* Back Navigation */}
            <button className='payment-back-navigation' onClick={() => whichCompToRender("CashTransactionComp")}>
              <ArrowLeft className='payment-back-icon' />
              go to transaction page
            </button>
          </div>

          {/* Spacer to prevent content from being hidden behind sticky footer */}
          <div className='payment-footer-spacer'></div>
        </div>
      </div>
      {displayReceipt && <InvoicePDF invoiceData={transactionDetails_} setPdfBlob={setPdfBlob} sendAsEmail={true} />}
      {print80mmReceipt && <ThermalReceipt transaction={transactionDetails_} />}
      <div ref={receiptRef} style={{ display: "none" }}></div>
    </>
  );
};

export default TransactionSuccess;

//Hook testing
export const useWebUSBPrinter = () => {
  const [device, setDevice] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const connectPrinter = async () => {
    try {
      if (!("usb" in navigator)) {
        console.error("WebUSB not supported on this browser.");
        return;
      }
      const selectedDevice = await navigator.usb.requestDevice({
        filters: [{ vendorId: 0x0416 }], // You can add { vendorId: 0xYOUR_VENDOR_ID } to filter printers
      });
      if (!selectedDevice) {
        console.error("❌ No device selected.");
        return;
      }

      await selectedDevice.open();
      await selectedDevice.selectConfiguration(1);
      await selectedDevice.claimInterface(0);

      setDevice(selectedDevice);
      setIsConnected(true);

      console.log("✅ Printer connected:", selectedDevice);
    } catch (error) {
      console.error("❌ Failed to connect printer:", error);
    }
  };

  const printText = async (text) => {
    if (!device) {
      console.error("❌ No device connected.");
      return;
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(text + "\n\n"); // text to send
      await device.transferOut(1, data); // 1 is usually the OUT endpoint
      console.log("🖨️ Sent to printer successfully");
    } catch (error) {
      console.error("❌ Failed to print:", error);
    }
  };

  const disconnectPrinter = async () => {
    if (device) {
      await device.close();
      setDevice(null);
      setIsConnected(false);
      console.log("🔌 Printer disconnected");
    }
  };

  return { connectPrinter, printText, disconnectPrinter, isConnected };
};

function generateBarcodeSvg(barcodeValue) {
  const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");

  JsBarcode(svgNode, String(barcodeValue || "000000000000"), {
    format: "CODE128",
    lineColor: "#000",
    width: 1.7,
    height: 32,
    displayValue: true,
    fontSize: 9,
    margin: 0,
  });

  svgNode.setAttribute("class", "barcode");

  return svgNode.outerHTML;
}

export function printProductLabels({ product, quantity = 1, labelHeightMm = 32, labelWidthMm = 70, paperWidthMm = 80 }) {
  const { name = "", brand = "", barcode = "" } = product;

  const safeQuantity = Math.max(1, Number(quantity) || 1);
  const barcodeSvg = generateBarcodeSvg(barcode);

  const labels = Array.from({ length: safeQuantity })
    .map(
      () => `
            <div class="label">
              <div class="product-name">${name}</div>
              <div class="brand">BRAND: ${brand}</div>
              ${barcodeSvg}
              <div class="powered-by">Empowered by storelense.com</div>
            </div>
          `
    )
    .join("");

  const printHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Product Labels</title>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    
          <style>
            @page {
              size: ${paperWidthMm}mm auto;
              margin: 0;
            }
    
            html, body {
              width: ${paperWidthMm}mm;
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              background: #fff;
              color: #000;
            }
    
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
    
            .labels-wrapper {
              width: ${paperWidthMm}mm;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 0;
              padding-top: 2mm;
            }
    
            .label {
              width: ${labelWidthMm}mm;
              height: ${labelHeightMm}mm;
              margin: 0 auto 3mm auto;
              padding: 2mm;
              box-sizing: border-box;
              overflow: hidden;
              border: 1px solid #000;
              border-radius: 4px;
    
              page-break-inside: avoid;
              break-inside: avoid;
    
              display: flex;
              flex-direction: column;
              justify-content: space-between;
            }
    
            .product-name {
              font-size: 15px;
              font-weight: 800;
              text-align: center;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 1mm;
              text-transform: uppercase;
            }
    
            .brand {
              text-align: center;
              font-size: 11px;
              border-top: 1px solid #000;
              border-bottom: 1px solid #000;
              padding: 1mm 0;
            }
    
            .barcode {
              width: 100%;
              height: 11mm;
              margin: 1mm 0;
            }
    
            .powered-by {
              text-align: center;
              font-size: 8px;
              color: #444;
              border-top: 1px dashed #000;
              padding-top: 1mm;
              letter-spacing: 0.2px;
            }
          </style>
        </head>
    
        <body>
          <div class="labels-wrapper">
            ${labels}
          </div>
    
          <script>
            window.onload = function () {
              setTimeout(function () {
                window.focus();
                window.print();
    
                setTimeout(function () {
                  window.close();
                }, 1000);
              }, 500);
            };
          </script>
        </body>
        </html>
      `;

  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "PRINT_LABEL",
        html: printHtml,
      })
    );
    return;
  }

  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    alert("Please allow popup to print label");
    return;
  }

  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();
}

//previous guy
